
import { defineComponent } from '@vue/composition-api';
import { mapActions, mapGetters } from 'vuex';
import IllinoisTabBar from '../TabBar.vue';
import IllinoisIcons from '../icons.vue';

type BetType = 'single' | 'multiple' | 'integral' | 'system' | 'simple';

interface TabItem {
  label: string;
  value: 'multiple' | 'integral' | 'system';
  disabled?: boolean;
}

export default defineComponent({
  name: 'IllinoisBetTypeSelector',
  components: { IllinoisTabBar, IllinoisIcons },
  computed: {
    ...mapGetters({
      getBet: 'cart/getBet',
      getSettings: 'cart/getSettings'
    }),

    activeTabValue(): 'multiple' | 'integral' | 'system' {
      const current: BetType = this.getBet?.type as BetType;
      if (current === 'integral') return 'integral';
      if (current === 'system') return 'system';
      return 'multiple';
    },

    tabItems(): TabItem[] {
      const items: TabItem[] = [];

      const oddsLength = Array.isArray(this.getBet?.odds)
        ? this.getBet.odds.length
        : 0;
      const typeStr = String(this.getBet?.type || 'single');
      const isIntegral = typeStr === 'integral';
      const isSingle = typeStr === 'single';
      const isSimple = typeStr === 'simple';

      const showSingle = isSingle && this.getSettings?.single;

      // Multiple tab: disabilitata quando non consentito cambiare
      const canSelectMultiple = !isIntegral && !isSimple && oddsLength > 1;
      items.push({
        label: showSingle
          ? (this.$t('single') as string)
          : (this.$t('multiple') as string),
        value: 'multiple',
        disabled: !canSelectMultiple && this.activeTabValue !== 'multiple'
      });

      // Integral tab: visibile se abilitato ma non cliccabile (richiesta)
      if (this.getSettings?.integral) {
        items.push({
          label: this.$t('integral') as string,
          value: 'integral',
          disabled: true
        });
      }

      // System tab: disabilitata secondo regole di selezione
      if (this.getSettings?.system) {
        const canSelectSystem =
          !isIntegral && !isSingle && oddsLength > 1 && oddsLength <= 10;

        items.push({
          label: this.$t('system') as string,
          value: 'system',
          disabled: !canSelectSystem && this.activeTabValue !== 'system'
        });
      }

      return items;
    }
  },
  methods: {
    ...mapActions({
      refreshCart: 'cart/refreshCart',
      setBetType: 'cart/setBetType',
      resetCart: 'cart/resetCart'
    }),
    t(key: string): string {
      return this.$t(key) as string;
    },
    async clearAll() {
      this.$store.dispatch('cart/setIsCartOpen', false);
      await this.resetCart();
    },
    async onTabChange(next: 'multiple' | 'integral' | 'system') {
      // Replica della logica originale: integral non selezionabile in questa variante
      if (next === 'integral') return;

      const oddsLength = Array.isArray(this.getBet?.odds)
        ? this.getBet.odds.length
        : 0;
      const currentType = (this.getBet?.type || 'single') as BetType;

      if (next === 'system') {
        if (
          currentType === 'integral' ||
          currentType === 'single' ||
          oddsLength === 1 ||
          oddsLength > 10
        ) {
          return;
        }
      }

      if (
        currentType !== 'integral' &&
        currentType !== 'simple' &&
        oddsLength > 1
      ) {
        await this.setBetType({ type: next });
        await this.refreshCart();
      }
    }
  }
});
